import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
//* NG PRIME
import { providePrimeNG } from 'primeng/config';
import { PrimengModule } from './PrimeNG/primeng.module';
import { TranslateWithFallbackPipe } from './pipes/translate-with-fallback.pipe';

//* Modules
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CardSoftwareLibraryModule, LOGIN_AUTH_SERVICE, LOGIN_CONFIG, SierraTheme } from '@card-software/library';
import { AngularMaterialModule } from './angular-material.module';
import { AppRoutingModule } from './app-routing.module';

//* Components
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AppComponent } from './app.component';
import { authInterceptor } from './auth.interceptor';

import { environment } from '../environments/environment';
import { InvoiceDetailModalComponent } from './components/_modals/invoice-detail-modal/invoice-detail-modal.component';
import { LotsAddModalComponent } from './components/_modals/lots-add-modal/lots-add-modal.component';
import { CardCompositionComponent } from './components/common/card-composition/card-composition.component';
import { InvoiceDetailComponent } from './components/invoices/invoice-detail/invoice-detail.component';
import { InvoicesComponent } from './components/invoices/invoices/invoices.component';
import { ReceiptsComponent } from './components/receipts/receipts/receipts.component';
import { ReceiptDetailComponent } from './components/receipts/receipt-detail/receipt-detail.component';
import { UserAuthenticationService } from './services/user-authentication.service';
import { PurchaseOrderDetailsComponent } from './components/purchase-orders/purchase-order-details/purchase-order-details.component';
import { PurchaseOrderComponent } from './components/purchase-orders/purchase-order/purchase-order/purchase-order.component';
import { ShipmentDetailsComponent } from './components/shipment/shipment-details/shipment-details.component';
import { ShipmentsComponent } from './components/shipment/shipments/shipments.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { WorkOrdersComponent } from './components/work-order/work-orders/work-orders.component';
import { WorkOrderDetailsComponent } from './components/work-order/work-order-details/work-order-details.component';
import { FabricDetailsPanelComponent } from './components/common/frabic-details-panel/fabric-details-panel.component';
import { ColorDetailsCardComponent } from './components/common/color-details-card/color-details-card.component';
import { ShipmentDetailModalComponent } from './components/_modals/shipment-detail-modal/shipment-detail-modal.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { FabricAliasManageModalComponent } from './components/_modals/fabric-alias-manage-modal/fabric-alias-manage-modal.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { NgxFileDropModule } from 'ngx-file-drop';
import { TranslateLocalizedPipe } from './pipes/translate-localized.pipe';
import { UtcToHondurasPipe } from './pipes/utc-to-honduras.pipe';
import { RequisitionDetailsComponent } from './components/requisition/requisition-details/requisition-details.component';
import { RequisitionsComponent } from './components/requisition/requisitions/requisitions.component';
import { RequisitionItemsTableComponent } from './components/requisition/requisition-items-table/requisition-items-table.component';
import { RequisitionQuoteDetailsComponent } from './components/requisition/quotation-details-modal/quotation-details-modal.component';
import { QuoteComparativeTableComponent } from './components/common/quote-comparative-table/quote-comparative-table.component';
import { QuotationsViewerComponent } from './components/requisition/quotations-viewer/quotations-viewer.component';
import { DeliveryNotesComponent } from './components/delivery-notes/delivery-notes/delivery-notes.component';
import { DeliveryNoteDetailsComponent } from './components/delivery-notes/delivery-note-details/delivery-note-details.component';
import { ClientPurchaseOrdersComponent } from './components/client-purchase-orders/client-purchase-orders.component';
import { ClientPurchaseOrderDetailsComponent } from './components/client-purchase-orders/client-purchase-order-details/client-purchase-order-details.component';
import { ClientPoAvailableInventoryComponent } from './components/client-purchase-orders/client-po-available-inventory/client-po-available-inventory.component';
import { CustomerOrderDispatchReportComponent } from './components/reports/customer-order-dispatch-report/customer-order-dispatch-report.component';
import { SITE_API_BASE_URL, errorInterceptor, siteInterceptor } from '@card-software/nexus-utilities';
@NgModule({
  declarations: [
    AppComponent,
    CardCompositionComponent,
    InvoiceDetailComponent,
    InvoicesComponent,
    ReceiptsComponent,
    ReceiptDetailComponent,
    InvoiceDetailModalComponent,

    PurchaseOrderComponent,
    FabricAliasManageModalComponent,
    PurchaseOrderDetailsComponent,
    ShipmentDetailsComponent,
    ShipmentsComponent,
    LotsAddModalComponent,
    WorkOrdersComponent,
    WorkOrderDetailsComponent,
    FabricDetailsPanelComponent,
    ColorDetailsCardComponent,
    ShipmentDetailModalComponent,
    UnauthorizedComponent,
    RequisitionsComponent,
    RequisitionDetailsComponent,
    RequisitionItemsTableComponent,
    RequisitionQuoteDetailsComponent,
    QuoteComparativeTableComponent,
    QuotationsViewerComponent,
    DeliveryNotesComponent,
    DeliveryNoteDetailsComponent,
    ClientPurchaseOrdersComponent,
    ClientPurchaseOrderDetailsComponent,
    ClientPoAvailableInventoryComponent,
    CustomerOrderDispatchReportComponent,
  ],
  imports: [
    TranslateWithFallbackPipe,
    AngularMaterialModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    CardSoftwareLibraryModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PrimengModule,
    NgxFileDropModule,
    TranslateLocalizedPipe,
    UtcToHondurasPipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
  ],
  exports: [TranslateWithFallbackPipe, TranslateLocalizedPipe, UtcToHondurasPipe],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra Order',
        contactEmail: 'it-support-sierra-textiles@card-software.com',
        redirectUrl: '/',
      },
    },
    provideHttpClient(withInterceptors([authInterceptor, siteInterceptor, errorInterceptor])),
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    MessageService,
    ConfirmationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
